"use client";

import { useCallback, useEffect, useState } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

interface QuerySummary {
  key: string;
  rawQueryKey: QueryKey;
  state: string;
  updatedAt: number | null;
  dataUpdatedAt: number | null;
  isFetching: boolean;
  isStale: boolean;
  errorMessage: string | null;
}

function stateColor(state: string, isFetching: boolean): string {
  if (isFetching) return "text-blue-500";
  if (state === "error") return "text-red-500";
  if (state === "success") return "text-emerald-500";
  return "text-muted-foreground";
}

export function QueryCachePanel() {
  const t = useTranslations("debug.queries");
  const queryClient = useQueryClient();
  const [queries, setQueries] = useState<QuerySummary[]>([]);
  const [filter, setFilter] = useState("");
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const [now, setNow] = useState<number | null>(null);
  const formatAge = (timestamp: number | null): string => {
    if (timestamp === null || timestamp === 0 || now === null) return "—";
    const seconds = Math.floor((now - timestamp) / 1000);
    return seconds < 60
      ? t("secondsAgo", { count: seconds })
      : t("minutesAgo", { count: Math.floor(seconds / 60) });
  };

  useEffect(() => {
    const update = () => setNow(Date.now());
    queueMicrotask(update);
    const timer = window.setInterval(update, 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const triggerFeedback = useCallback((msg: string) => {
    setActionFeedback(msg);
    setTimeout(() => setActionFeedback(null), 2000);
  }, []);

  useEffect(() => {
    if (!queryClient) return;
    function refresh() {
      const cache = queryClient.getQueryCache();
      const all = cache.getAll();
      const summaries: QuerySummary[] = all.map((query) => {
        const s = query.state;
        return {
          key: JSON.stringify(query.queryKey),
          rawQueryKey: query.queryKey,
          state: s.status,
          updatedAt: s.dataUpdatedAt || null,
          dataUpdatedAt: s.dataUpdatedAt || null,
          isFetching: query.state.fetchStatus === "fetching",
          isStale: query.isStale(),
          errorMessage: s.error instanceof Error ? s.error.message : null,
        };
      });
      setQueries(summaries);
    }

    refresh();
    const unsubscribe = queryClient.getQueryCache().subscribe(refresh);
    return () => unsubscribe();
  }, [queryClient]);

  const handleRefetchAll = useCallback(() => {
    void queryClient.refetchQueries();
    triggerFeedback(t("feedback.refetchingAll"));
  }, [queryClient, t, triggerFeedback]);

  const handleInvalidateAll = useCallback(() => {
    void queryClient.invalidateQueries();
    triggerFeedback(t("feedback.invalidatedAll"));
  }, [queryClient, t, triggerFeedback]);

  const handleRefetchSingle = useCallback(
    (queryKey: QueryKey) => {
      void queryClient.refetchQueries({ queryKey });
      triggerFeedback(t("feedback.refetched"));
    },
    [queryClient, t, triggerFeedback],
  );

  const handleInvalidateSingle = useCallback(
    (queryKey: QueryKey) => {
      void queryClient.invalidateQueries({ queryKey });
      triggerFeedback(t("feedback.invalidated"));
    },
    [queryClient, t, triggerFeedback],
  );

  const handleRemoveSingle = useCallback(
    (queryKey: QueryKey) => {
      queryClient.removeQueries({ queryKey });
      triggerFeedback(t("feedback.removed"));
    },
    [queryClient, t, triggerFeedback],
  );

  const filtered = filter
    ? queries.filter((q) => q.key.toLowerCase().includes(filter.toLowerCase()))
    : queries;

  if (queries.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        {t("empty")}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Search & Global Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder={t("filterPlaceholder")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full text-sm px-2.5 py-1 rounded border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/50"
          />
          <span className="text-sm text-muted-foreground shrink-0 font-mono">
            {filtered.length}
            {"/"}
            {queries.length}
          </span>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {actionFeedback && (
            <span className="rounded border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-sm font-medium text-emerald-500">
              {actionFeedback}
            </span>
          )}
          <button
            type="button"
            onClick={handleRefetchAll}
            className="cursor-pointer rounded border border-border bg-muted/50 px-2.5 py-1 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            title={t("refetchAllTitle")}
          >
            {t("refetchAll")}
          </button>
          <button
            type="button"
            onClick={handleInvalidateAll}
            className="cursor-pointer rounded border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-sm font-medium text-amber-600 transition-colors hover:bg-amber-500/20 dark:text-amber-400"
            title={t("invalidateAllTitle")}
          >
            {t("invalidateAll")}
          </button>
        </div>
      </div>

      {/* Query List */}
      <div className="space-y-1">
        {filtered.map((q) => (
          <div
            key={q.key}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-1.5 px-2.5 rounded border border-border/40 bg-muted/10 hover:bg-muted/30 transition-colors group text-sm font-mono"
          >
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <span
                className={cn(
                  "shrink-0 w-16 py-0.5 text-sm font-semibold",
                  stateColor(q.state, q.isFetching),
                )}
              >
                {q.isFetching
                  ? t("states.fetching")
                  : q.state === "error"
                    ? t("states.error")
                    : q.state === "success"
                      ? t("states.success")
                      : q.state === "pending"
                        ? t("states.pending")
                        : q.state}
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-foreground/90 break-all">{q.key}</span>
                {q.errorMessage && (
                  <div className="mt-0.5 text-sm text-red-400">
                    {q.errorMessage}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-border/30">
              <div className="flex items-center gap-1.5 text-sm">
                <span className="text-muted-foreground/60">
                  {formatAge(q.dataUpdatedAt)}
                </span>
                {q.isStale && !q.isFetching && (
                  <span className="rounded bg-amber-500/10 px-1 py-0.2 text-sm text-amber-500/80">
                    {t("stale")}
                  </span>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleRefetchSingle(q.rawQueryKey)}
                  className="cursor-pointer rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 text-sm text-foreground transition-colors hover:bg-muted"
                  title={t("refetchTitle")}
                >
                  {"↻"}
                </button>
                <button
                  type="button"
                  onClick={() => handleInvalidateSingle(q.rawQueryKey)}
                  className="cursor-pointer rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-sm text-amber-600 transition-colors hover:bg-amber-500/20 dark:text-amber-400"
                  title={t("invalidateTitle")}
                >
                  {"🗑️"}
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveSingle(q.rawQueryKey)}
                  className="cursor-pointer rounded border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 text-sm text-red-500 transition-colors hover:bg-red-500/20"
                  title={t("removeTitle")}
                >
                  {"❌"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
